import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { search as getStudentCourseSessionEnrollment } from 'store/member/schedule'
import { Box, LinearProgress } from '@mui/material'
import { useMember, useSession } from 'components/Providers'
import ScheduleTable from 'components/Schedule'
import NoRecords from 'components/NoRecords'
import MarkingPeriodField from 'components/MarkingPeriodField'
import Header from 'components/Header'
import usePrintArea from 'utils/print'

export const Schedule = () => {
  const dispatch = useDispatch()
  const member = useMember()
  const { currentMarkingPeriodId } = useSelector(state => state.lookup);

  const [studentCourseSessionEnrollment, setStudentCourseSessionEnrollment] = useState()
  const [scheduledSessions, setScheduledSessions] = useState()
  const [unScheduledSessions, setUnscheduledSessions] = useState()
  const [startRenderList, setStartRenderList] = useState()
  const [errorMessage, setErrorMessage] = useState()
  const { academicYear } = useSession();
  const isLoading = useSelector(state => state.member.scheduleLoading);

  const studentCourseSessionEnrollmentFetch = async () => {
    if (!member?.id || !member?.person?.type || currentMarkingPeriodId === null) {
      setStudentCourseSessionEnrollment([]);
      setErrorMessage(<NoRecords message="Loading or missing member information" />);
      return;
    }

    try {
      const action = getStudentCourseSessionEnrollment({
        memberId: member.id,
        type: member.person.type,
        markingPeriodId: currentMarkingPeriodId
      });
      const data = await dispatch(action);

      if (data?.payload?.length > 0) {
        setStudentCourseSessionEnrollment(data.payload);
        setErrorMessage('');
      } else {
        setStudentCourseSessionEnrollment([]);
        setErrorMessage(<NoRecords message="No Assigned Courses" />);
      }
    } catch (error) {
      setStudentCourseSessionEnrollment([]);
      setErrorMessage(<NoRecords message="Error loading courses" />);
    }
  };

  useEffect(() => {
    studentCourseSessionEnrollmentFetch()
  }, [member.id, currentMarkingPeriodId])

  useEffect(() => {
    if (studentCourseSessionEnrollment?.length > 0) {
      let temp = []
      const rawScheduledSessions = studentCourseSessionEnrollment?.filter(
        item => item.courseSession?.schedules?.length > 0,
      )
      rawScheduledSessions.forEach(item => {
        item.courseSession.schedules.forEach(scheduleItem => {
          const newItem = {
            id: item.courseSession.id,
            name: item.courseSession.name,
            startTime: scheduleItem.period.startTime,
            endTime: scheduleItem.period.endTime,
            periodId: scheduleItem.period.id,
            dayOfWeek: scheduleItem.day,
            room: item?.courseSession?.room?.id
              ? item?.courseSession?.room
              : null,
            teachers: item?.courseSession?.teachers
              ? item.courseSession.teachers.map(teacher =>
                `${teacher.lastName}, ${teacher.firstName}`
              ).join(' | ')
              : null,
          }
          temp = addSessionToSchedule(newItem, temp)
        })
      })
      setScheduledSessions(temp)
      setErrorMessage('')
      setUnscheduledSessions(
        studentCourseSessionEnrollment?.filter(item => item.courseSession?.schedules?.length === 0),
      )

    } else if (studentCourseSessionEnrollment?.length === 0) {
      setErrorMessage(<NoRecords message='No Assigned Courses' />)
    }
  }, [studentCourseSessionEnrollment])

  const addSessionToSchedule = (newItem, storage) => {
    if (storage.length === 0) {
      storage.push({
        startTime: newItem.startTime,
        endTime: newItem.endTime,
        dayOfWeek: newItem.dayOfWeek,
        periodId: newItem.periodId,
        list: [{ id: newItem.id, name: newItem.name, room: newItem.room, teachers: newItem.teachers }],
      })
      return storage
    }
    let existsIndex = -1
    storage.forEach((item, index) => {
      if (
        item.startTime === newItem.startTime &&
        item.endTime === newItem.endTime &&
        item.dayOfWeek === newItem.dayOfWeek &&
        item.periodId === newItem.periodId
      ) {
        if (existsIndex === -1) existsIndex = index
      }
    })

    if (existsIndex === -1) {
      storage.push({
        startTime: newItem.startTime,
        endTime: newItem.endTime,
        dayOfWeek: newItem.dayOfWeek,
        periodId: newItem.periodId,
        list: [{ id: newItem.id, name: newItem.name, room: newItem.room, teachers: newItem.teachers }],
      })
      return storage
    } else {
      const temp = storage[existsIndex]
      temp.list.push({ id: newItem.id, name: newItem.name, room: newItem.room, teachers: newItem.teachers })
      storage[existsIndex] = temp
      return storage
    }
  }

  useEffect(() => {
    setStartRenderList(true)
  }, [scheduledSessions])

  const studentName = `${member?.person?.firstName ?? ''} ${member?.person?.lastName ?? ''}`.trim()

  const { printRef, print } = usePrintArea({
    orientation: 'landscape',
    title: `Schedule - ${studentName}`.trim(),
  })

  return (
    <Box>
      <MarkingPeriodField
        label="Marking Period"
        academicYearId={academicYear?.id}
      />

      {isLoading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress color="success" />
        </Box>
      )}

      {!isLoading && errorMessage && (
        <Box sx={{ mt: 4 }}>
          {errorMessage}
        </Box>
      )}

      {!isLoading && !startRenderList && !errorMessage && (
        <LinearProgress sx={{ mt: 2 }} color="success" />
      )}

      {!isLoading && startRenderList && scheduledSessions && !errorMessage && (
        <Box ref={printRef}>
          <Box sx={{ pr: 0.5 }}>
            <Header print small printfunc={print} />
          </Box>
          <ScheduleTable
            unScheduledSessions={unScheduledSessions}
            scheduledSessions={scheduledSessions}
          />
        </Box>
      )}
    </Box>
  );

}

